import React, {
  useMemo, useRef, useState, useEffect,
} from 'react';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import moment from 'moment';

import {
  BarChart,
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { showInfoToast } from '../../../../shared/utils/toastManager';

import CustomChartLegend from './CustomChartLegend';
import CustomChartToolTip from './CustomChartToolTip';
import { BoxShadow } from '../../../../shared/GlobalStyles';
import Spinner from '../../../../shared/_TBComponents/Spinner';
import Text from '../../../../shared/_TBComponents/Text';

const ChartWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  position: relative;
  border: solid 1px #E0E0E0;
  border-radius: 8px;
  margin-bottom: 30px;
  padding-bottom: 12px;
`;

const ChartLineOverlay = styled('div')`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 10;
  background: transparent;
  pointer-events: ${(props) => (props.active ? 'auto' : 'none')};
`;

const ExerciseHeader = styled('div')`
  display: flex;
  background: #fcfcfc;
  padding: 14px 16px;
  border-bottom: solid 1px #E0E0E0;
  border-radius: 8px 8px 0 0;
  min-height: 56px;
`;

const FilterWrapper = styled('div')`
  display: flex;
  justify-content: center;
  filter: ${(props) => (props.isBlurred ? 'blur(1.2px)' : null)};
`;

const PrintWrapper = styled('div')`
  @media print {
    @page {
      size: A4 landscape;
      margin: 6px;
    }
    zoom: 0.9;

    @media (min-width: 1440px) {
      zoom: 0.75;
    }

    @media (min-width: 1920px) {
      zoom: 0.5;
    }
  }
`;

const ChartContainer = styled('div')`
  flex: 1;
  flex-grow: 1;
  min-width: 0; 
  height: ${(props) => (props.isLoading ? '355px' : '90%')};
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
  filter: ${(props) => (props.isBlurred ? 'blur(1.2px)' : null)};
  pointer-events: ${(props) => (props.isBlurred ? 'none' : 'auto')};

  // Transparent scrollbar
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    width: 0;
    height: 0;
  }

  @media print {
    overflow: hidden !important;
    max-width: 100% !important;
  }
`;

const ChartScrollWrapper = styled('div')`
  position: relative;
  min-width: ${(props) => props.minWidth}px;
  height: 400px;

  @media print {
    overflow: hidden !important;
    max-width: 100% !important;
  }
`;

const YAxisLeftContainer = styled('div')`
  position: sticky;
  left: 0; 
  z-index: 1; 
  background-color: '#fff';
`;

const YAxisRightContainer = styled('div')`
  position: sticky;
  right: 0;
  z-index: 1;
  background-color: #fff;
`;

const NoDataModal = styled('div')`
  position: absolute;
  top: 50%;
  left: 48.5%;
  transform: translate(-50%, -50%);
  height: auto;
  width: 400px;
  background-color: #FFFFFF;
  border-radius: 8px;
  box-shadow: ${BoxShadow};
  font-family: 'Nunito Sans';
  font-size: 19px;
  color: #424242;
  padding: 15px;
  text-align: center;
`;

const ModalTitle = styled('div')`
  font-weight: bold;
`;

const OverviewChart = ({
  exerciseData,
  bucketSize,
  rangeMax,
  rangeMin,
  selectedAthlete,
  isLoading,
  readyPrint,
  viewOnly,
}) => {
  const containerRef = useRef(null);
  const MIN_BAR_HEIGHT = 8;
  const CHART_HEIGHT = 400;

  const [hoveredBucketKey, setHoveredBucketKey] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [pinnedBucketKey, setPinnedBucketKey] = useState(null);
  const [pinnedIndex, setPinnedIndex] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const isSideBarExpanded = useSelector(
    (state) => state.sportsScience.data.isSideBarExpanded,
  );

  useEffect(() => {
    setIsTransitioning(true);
    const timeout = setTimeout(() => {
      setIsTransitioning(false);
    }, 100);

    return () => clearTimeout(timeout);
  }, [bucketSize, rangeMin, rangeMax]);

  const activeBucketKey = pinnedBucketKey ?? hoveredBucketKey;
  const activeIndex = pinnedIndex ?? hoveredIndex;

  const getActiveBuckets = () => {
    const showHundredPlus = rangeMax > 100;
    const upperLimit = showHundredPlus ? 100 : rangeMax;

    const baseBuckets = Array.from(
      { length: Math.ceil((upperLimit - rangeMin) / bucketSize) },
      (_, i) => {
        const start = rangeMin + i * bucketSize;
        return {
          key: `r${start}to${start + bucketSize}`,
          label: `${start}–${start + bucketSize}%`,
        };
      },
    );

    return showHundredPlus
      ? [...baseBuckets, { key: 'r100plus', label: '100%+' }]
      : baseBuckets;
  };

  const activeBuckets = getActiveBuckets();

  // Builds raw bar chart data for each date and intensity bucket
  const rawFormattedData = useMemo(() => {
    if (!exerciseData?.dates?.length || rangeMin == null || rangeMax == null) {
      return [{
        date: '',
        tonnage: 0,
        ...Object.fromEntries(
          activeBuckets.flatMap(({ key }) => [
            [`completed_${key}_raw`, 0],
          ]),
        ),
      }];
    }

    return exerciseData.dates.map((day) => {
      const formattedDate = moment(day.date).format('MMM D');
      const athleteData = selectedAthlete?.id
        ? day.athleteData?.find((a) => a.id === selectedAthlete.id)
        : day.groupData;

      const bucketEntries = activeBuckets.reduce((acc, { key }) => {
        const reps = athleteData?.completed?.[key]?.reps ?? 0;
        const tonnage = athleteData?.completed?.[key]?.tonnage ?? 0;
        return {
          ...acc,
          [`completed_${key}_raw`]: reps,
          [`completed_${key}_tonnage`]: tonnage,
          tonnage: (acc.tonnage ?? 0) + tonnage,
        };
      }, {});

      return {
        date: formattedDate,
        ...bucketEntries,
      };
    });
  }, [exerciseData, rangeMin, rangeMax, bucketSize, selectedAthlete, activeBuckets]);

  // Checks if there is any real data (not zero values) to display
  const hasData = useMemo(() => rawFormattedData.some(
    (entry) => Object.keys(entry).some((key) => key.endsWith('_raw') && entry[key] > 0),
  ), [rawFormattedData]);

  const hasDatesButNoValues = exerciseData?.dates?.length > 0 && !hasData;

  // Two blocks below get highest values from data and produce clean intervals for ticks
  const dynamicMaxY = useMemo(() => {
    if (!hasData) return 10;

    const max = rawFormattedData.reduce((acc, entry) => {
      const values = Object.entries(entry)
        .filter(([key]) => key.endsWith('_raw') && typeof entry[key] === 'number')
        .map(([, val]) => val);
      return Math.max(acc, ...values);
    }, 0);

    return Math.max(10, max);
  }, [rawFormattedData, hasData]);

  const dynamicMaxTonnageY = useMemo(() => {
    if (!hasData) return 5000;

    const max = rawFormattedData.reduce((acc, entry) => Math.max(acc, entry.tonnage ?? 0), 0);

    if (max === 0) return 5000;
    if (max <= 5000) return 5000;
    if (max <= 10000) return 10000;
    if (max <= 20000) return 20000;
    if (max <= 50000) return 50000;

    return 100000;
  }, [rawFormattedData, hasData]);

  const getFixedYAxisTicks = (maxY) => {
    const steps = 5;
    if (maxY === 0) return [0];

    const rawInterval = maxY / steps;
    const base = 10 ** Math.floor(Math.log10(rawInterval));
    const cleanSteps = [1, 2, 4, 5, 6, 8, 10];

    let chosenInterval = cleanSteps.find(
      (step) => step * steps >= maxY,
    );
    if (!chosenInterval) {
      const fallback = Math.ceil(rawInterval / base) * base;
      chosenInterval = fallback;
    }

    return Array.from({ length: steps + 1 }, (_, i) => i * chosenInterval);
  };

  const minBarValue = useMemo(() => {
    const scale = dynamicMaxY / CHART_HEIGHT;
    return scale * MIN_BAR_HEIGHT;
  }, [dynamicMaxY]);

  // Uses the raw data and then adds the min bar height from above to any zero values
  const formattedData = useMemo(() => rawFormattedData.map((entry) => {
    const newEntry = { date: entry.date, tonnage: 0 };

    activeBuckets.forEach(({ key }) => {
      const reps = entry[`completed_${key}_raw`] ?? 0;

      newEntry[`completed_${key}`] = reps === 0 ? minBarValue : reps;
      newEntry[`completed_${key}_raw`] = reps;
      // Show line on zero values
      newEntry[`tonnage_${key}`] = entry[`completed_${key}_tonnage`] ?? 0;
      newEntry.tonnage += entry[`completed_${key}_tonnage`] ?? 0;
    });

    return newEntry;
  }), [rawFormattedData, dynamicMaxY, activeBuckets]);

  // Size bars dynamically based on total bar count
  const bucketCount = (rangeMax - rangeMin) / bucketSize;
  const totalBars = formattedData.length * bucketCount;

  const getBarSize = (barCount, printMode) => {
    if (barCount <= 10) return 50;
    if (barCount <= 20) return 35;
    if (barCount <= 40) return 25;

    if (printMode) {
      const maxPrintableWidth = 1100;
      const minBarSize = 5;
      const maxBarSize = 15;
      const calculatedSize = Math.floor(maxPrintableWidth / barCount);
      return Math.max(minBarSize, Math.min(calculatedSize, maxBarSize));
    }

    return 35;
  };

  const barSize = getBarSize(totalBars, readyPrint);
  const yAxisWidth = 90;

  // This allows us to fit the custom Y-Axis and add space between bar groups
  const numBarsPerGroup = activeBuckets.length;
  const chartMinWidth = formattedData.length * (
    barSize * numBarsPerGroup + 5) + yAxisWidth + (readyPrint ? 150 : 0);

  // Group and bar color logic for different buckets
  const baseColors = ['#FBCC53', '#8006F4', '#FC4A1A', '#7A431C', '#1ADAFC'];

  const barGradients = activeBuckets.flatMap(({ key }, index) => {
    const baseColor = baseColors[index % baseColors.length];
    return [
      <linearGradient key={`completed-${key}`} id={`completed-${key}`} x1='0' y1='1' x2='0' y2='0'>
        <stop offset='0%' stopColor={baseColor} stopOpacity={0.6} />
        <stop offset='100%' stopColor={baseColor} stopOpacity={1} />
      </linearGradient>,
    ];
  });

  const barGroups = activeBuckets.flatMap(({ key, label }, index) => {
    const baseColor = baseColors[index % baseColors.length];
    const isHovering = hasData && hoveredBucketKey && activeIndex !== null;
    const isDimmed = isHovering && activeBucketKey !== key;
    const opacity = isDimmed ? 0.5 : 1;

    return [
      <Bar
        key={`completed-${key}`}
        dataKey={`completed_${key}`}
        name={`Completed ${label}`}
        fill={`url(#completed-${key})`}
        barSize={barSize}
        radius={[5, 5, 0, 0]}
        stroke={baseColor}
        strokeWidth={0}
        isAnimationActive={false}
        fillOpacity={opacity}
        onMouseEnter={(_, i) => {
          if (!pinnedBucketKey) {
            setHoveredBucketKey(key);
            setHoveredIndex(i);
          }
        }}
        onMouseLeave={() => {
          if (!pinnedBucketKey) {
            setHoveredBucketKey(null);
            setHoveredIndex(null);
          }
        }}
        onClick={(_, i) => {
          if (pinnedBucketKey === key && pinnedIndex === i) {
            setPinnedBucketKey(null);
            setPinnedIndex(null);
          } else if (hasData) {
            setPinnedBucketKey(key);
            setPinnedIndex(i);
            showInfoToast('The tonnage line has been pinned. To unpin, just click anywhere within the graph.');
          }
        }}
      />,
    ];
  });

  return (
    <>
      <ChartWrapper>
        <ExerciseHeader>
          <Text
            styledTextProps={{ fontSize: '18px', fontWeight: 600 }}
          >
            {exerciseData?.exerciseName}
          </Text>
        </ExerciseHeader>
        {isTransitioning ? (
          <ChartContainer isLoading={isLoading}>
            <Spinner />
          </ChartContainer>
        ) : (
          <>
            <FilterWrapper isBlurred={!hasData}>
              <CustomChartLegend
                rangeMin={rangeMin}
                rangeMax={rangeMax}
                bucketSize={bucketSize}
                baseColors={baseColors}
                activeBuckets={activeBuckets}
              />
            </FilterWrapper>
            {/* This is a workaround to add a sticky Y-Axis that works with scroll */}
            <PrintWrapper isSideBarExpanded={isSideBarExpanded}>
              <FilterWrapper isBlurred={!hasData}>
                <YAxisLeftContainer>
                  <BarChart
                    data={formattedData}
                    width={80}
                    height={400}
                    margin={{
                      top: 30, bottom: 50, right: 0, left: -10,
                    }}
                  >
                    <XAxis dataKey='date' width={0} />
                    <YAxis
                      width={yAxisWidth}
                      tickLine={false}
                      axisLine={false}
                      interval={0}
                      tick={{
                        fontSize: 16,
                        fontFamily: 'Nunito Sans',
                        fill: '#9E9E9E',
                      }}
                      label={({ viewBox }) => {
                        const {
                          x, y, width, height,
                        } = viewBox;
                        const centerX = x + width / 2;
                        const centerY = y + height / 2;

                        return (
                          <text
                            x={centerX + 100}
                            y={centerY}
                            textAnchor='middle'
                            transform={`rotate(270, ${centerX}, ${centerY})`}
                          >
                            <tspan
                              x={centerX + 100}
                              dy={-16}
                              dx={-100}
                              style={{
                                fontSize: 16,
                                fontFamily: 'Nunito Sans',
                                fill: '#9E9E9E',
                              }}
                            >
                              Reps Completed
                            </tspan>
                            <tspan
                              x={centerX + 100}
                              dy={16}
                              dx={-100}
                              style={{
                                fontSize: 12,
                                fontFamily: 'Nunito Sans',
                                fill: '#9E9E9E',
                              }}
                            >
                              (Bars)
                            </tspan>
                          </text>
                        );
                      }}
                      domain={[0, dynamicMaxY]}
                      ticks={getFixedYAxisTicks(dynamicMaxY)}
                      allowDecimals
                    />
                  </BarChart>
                </YAxisLeftContainer>
                {/* This is the actual graph being displayed */}
                <ChartContainer ref={containerRef}>
                  <ChartScrollWrapper minWidth={chartMinWidth}>
                    <ChartLineOverlay
                      active={!!pinnedBucketKey}
                      onClick={() => {
                        setPinnedBucketKey(null);
                        setPinnedIndex(null);
                        setHoveredBucketKey(null);
                        setHoveredIndex(null);
                      }}
                    />
                    {/* Width logic here is the key to print sizing */}
                    <ResponsiveContainer width={readyPrint ? chartMinWidth : '100%'} height='100%'>
                      <ComposedChart
                        data={formattedData}
                        barGap={readyPrint ? 1 : 2}
                        barCategoryGap={readyPrint ? 1 : 15}
                        margin={{
                          top: 30, bottom: 50, right: 0, left: 0,
                        }}
                      >
                        <defs>{barGradients}</defs>
                        <CartesianGrid vertical={false} stroke='#E0E0E0' strokeDasharray='0' />
                        <XAxis
                          dataKey='date'
                          angle={-45}
                          textAnchor='end'
                          dy={10}
                          tick={{
                            fontSize: 16,
                            fontFamily: 'Nunito Sans',
                            fill: '#9E9E9E',
                          }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <YAxis
                          hide={false}
                          width={0}
                          tickLine={false}
                          axisLine={false}
                          interval={0}
                          domain={[0, dynamicMaxY]}
                          ticks={getFixedYAxisTicks(dynamicMaxY)}
                          allowDecimals
                        />
                        <YAxis
                          yAxisId='right'
                          hide={false}
                          width={0}
                          tickLine={false}
                          axisLine={false}
                          interval={0}
                          orientation='right'
                          domain={[0, dynamicMaxTonnageY]}
                          ticks={getFixedYAxisTicks(dynamicMaxTonnageY)}
                          allowDecimals
                        />
                        {barGroups}
                        {activeBucketKey && hasData && (
                          <Line
                            key={`line-${activeBucketKey}`}
                            dataKey={`tonnage_${activeBucketKey}`}
                            yAxisId='right'
                            type='monotone'
                            stroke={(() => {
                              const index = activeBuckets.findIndex(
                                (b) => b.key === activeBucketKey,
                              );
                              return `${baseColors[index % baseColors.length]}CC`;
                            })()}
                            strokeWidth={3}
                            isAnimationActive={false}
                            dot={({ cx, cy }) => {
                              const bucketIndex = activeBuckets.findIndex(
                                (b) => b.key === activeBucketKey,
                              );
                              const color = `${baseColors[bucketIndex % baseColors.length]}CC`;
                              return (
                                <circle
                                  cx={cx}
                                  cy={cy}
                                  r={5}
                                  fill={color}
                                  stroke='#fff'
                                  strokeWidth={2}
                                  style={{ filter: 'drop-shadow(0 0 4px white)' }}
                                />
                              );
                            }}
                            activeDot={false}
                            connectNulls
                          />
                        )}
                        {!readyPrint && hasData && (
                          <Tooltip
                            cursor={false}
                            content={(props) => (
                              <CustomChartToolTip
                                {...props}
                                buckets={activeBuckets}
                                barSize={barSize}
                                chartWidth={chartMinWidth}
                                hoveredBucketKey={activeBucketKey}
                              />
                            )}
                          />
                        )}
                      </ComposedChart>
                    </ResponsiveContainer>
                  </ChartScrollWrapper>
                </ChartContainer>
                {/* This is the sticky right side Y-Axis and only used for layout */}
                <YAxisRightContainer>
                  <BarChart
                    data={formattedData}
                    width={100}
                    height={400}
                    margin={{
                      top: 30, bottom: 50, right: 0, left: -70,
                    }}
                  >
                    <XAxis dataKey='date' height={0} />
                    <YAxis
                      yAxisId='right'
                      width={yAxisWidth}
                      tickLine={false}
                      axisLine={false}
                      interval={0}
                      tick={{
                        fontSize: 16,
                        fontFamily: 'Nunito Sans',
                        fill: '#9E9E9E',
                        textAnchor: 'start',
                      }}
                      label={({ viewBox }) => {
                        const {
                          x, y, width, height,
                        } = viewBox;
                        const centerX = x + width / 2;
                        const centerY = y + height / 2;

                        return (
                          <text
                            x={centerX + 100}
                            y={centerY}
                            textAnchor='middle'
                            transform={`rotate(270, ${centerX + 110}, ${centerY})`}
                          >
                            <tspan
                              x={centerX + 100}
                              dy={-8}
                              dx={25}
                              style={{
                                fontSize: 16,
                                fontFamily: 'Nunito Sans',
                                fill: '#9E9E9E',
                              }}
                            >
                              Tonnage
                            </tspan>
                            <tspan
                              x={centerX + 100}
                              dy={16}
                              dx={25}
                              style={{
                                fontSize: 12,
                                fontFamily: 'Nunito Sans',
                                fill: '#9E9E9E',
                              }}
                            >
                              (Line on Hover)
                            </tspan>
                          </text>
                        );
                      }}
                      domain={[0, dynamicMaxTonnageY]}
                      ticks={getFixedYAxisTicks(dynamicMaxTonnageY)}
                      allowDecimals
                      padding={{ bottom: 30 }}
                    />
                  </BarChart>
                </YAxisRightContainer>
              </FilterWrapper>
            </PrintWrapper>
            {!isLoading && (
              exerciseData?.dates?.length === 0 || hasDatesButNoValues) && (
              <NoDataModal>
                <ModalTitle>
                  {exerciseData?.dates?.length === 0
                    ? 'No Data Available'
                    : 'No Data for Selected Range'}
                </ModalTitle>
                <Text
                  styledTextProps={{
                    fontWeight: 300,
                    fontSize: '19px',
                    color: '#424242',
                    textAlign: 'center',
                  }}
                >
                  {exerciseData?.dates?.length === 0
                    ? (
                      <>
                        There is no data available for
                        {' '}
                        <strong>{exerciseData.exerciseName}</strong>
                        , please adjust your selection above.
                      </>
                    )
                    : (
                      <>
                        There is no data available for
                        {' '}
                        <strong>{exerciseData.exerciseName}</strong>
                        {' '}
                        within the selected intensity buckets.
                        {' '}
                        {viewOnly
                          ? ' Please select another athlete.'
                          : ' Please adjust your filter selection.'}
                      </>
                    )}
                </Text>
              </NoDataModal>
            )}
          </>
        )}
      </ChartWrapper>
    </>
  );
};

OverviewChart.propTypes = {
  exerciseData: PropTypes.shape({
    exerciseName: PropTypes.string,
    dates: PropTypes.arrayOf(
      PropTypes.shape({
        date: PropTypes.string,
        groupData: PropTypes.shape({
          assigned: PropTypes.shape({
            tonnage: PropTypes.number,
          }),
          completed: PropTypes.shape({
            tonnage: PropTypes.number,
          }),
        }),
      }),
    ),
  }),
  bucketSize: PropTypes.number.isRequired,
  rangeMax: PropTypes.number.isRequired,
  rangeMin: PropTypes.number.isRequired,
  selectedAthlete: PropTypes.shape({
    id: PropTypes.number,
  }),
  isLoading: PropTypes.bool.isRequired,
  readyPrint: PropTypes.bool,
  viewOnly: PropTypes.bool,
};

OverviewChart.defaultProps = {
  exerciseData: {
    exerciseName: '',
    dates: [],
  },
  selectedAthlete: null,
  readyPrint: false,
  viewOnly: false,
};

export default OverviewChart;
